/**
 * Parameter builders for the Current-Week drilldown modal.
 *
 * The modal can pivot on either entity:
 *   - ``schedule`` (POLineSchedule list at ``/purchasing/schedules/``)
 *     — used for window-level tiles (due / open / arrears) and pending
 *   - ``grn`` (GoodsReceiptNote list at ``/purchasing/grns/``) — used for
 *     by-day / OTD-mix segment clicks because the chart bars are
 *     **GRN-keyed by receipt_date with on_time_status**, not schedule rows.
 *     Earlier the drilldown queried schedules by schedule_date which
 *     produced wildly different counts (chart 172 late vs modal 505
 *     "delivered") and had no on_time_status filter — fixed here.
 *
 * Every mode applies:
 *   - dashboard scope (customer / supplier / part / erp_order_type)
 *   - exclude_line_zero=true (line-0 anchors never count toward GRN-day
 *     buckets or schedule lists)
 */

export type CurrentWeekDrilldownMode =
  | 'due_this_week'      // Cost — every schedule_date in window (denominator)
  | 'open_this_week'     // Cost / Open Schedule Lines — outstanding=true
  | 'received_this_week' // Cost / Received — status=shipped (proxy)
  | 'arrears'            // Cost / Arrears — past-due AND open
  | 'otif_this_week'     // Delivery / OTIF — scored (passed+failed) this week
  | 'otd_this_week'      // Delivery / OTD  — scored (passed+failed) this week
  | 'day_on_time'
  | 'day_early'
  | 'day_late'
  | 'day_pending'
  | 'mix_on_time'
  | 'mix_early'
  | 'mix_late'
  | 'mix_otif_pass'         // OTIF — passed (in-full + within tolerance)
  | 'mix_otif_pending'      // OTIF — pending (still within tolerance window)
  | 'mix_otif_fail';        // OTIF — failed (past window without passing,
                            //               or delivered but didn't meet OTIF)

export interface CurrentWeekDrilldownFilters {
  customer_id?: number | string;
  supplier_id?: number | string;
  part_number?: string;
  erp_order_type: '1' | '4';
}

export interface BuildParamsArgs {
  mode: CurrentWeekDrilldownMode;
  filters: CurrentWeekDrilldownFilters;
  weekStartIso: string;
  weekEndIso: string;
  otifStartIso: string;
  otifEndIso: string;
  todayIso: string;
  dayIso?: string;
  /** Arrears grace buffer (days). A schedule only counts as arrears once it
   *  is more than this many days past due — mirrors the backend tile. */
  arrearsBufferDays?: number;
}

/** Shift an ISO date (YYYY-MM-DD) back by ``days`` and return ISO. */
export function isoMinusDays(iso: string, days: number): string {
  if (!days) return iso;
  const d = new Date(iso + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}

export const MODE_TITLES: Record<CurrentWeekDrilldownMode, string> = {
  due_this_week:      'Schedules due this week',
  open_this_week:     'Open schedule lines this week',
  received_this_week: 'Received schedules this week',
  arrears:            'Arrears — past-due open schedules',
  otif_this_week:     'OTIF — schedules scored this week',
  otd_this_week:      'OTD — schedules scored this week',
  day_on_time:        'On-time deliveries',
  day_early:          'Early deliveries',
  day_late:           'Late deliveries',
  day_pending:        'Pending schedules',
  mix_on_time:        'On-time deliveries — week to date',
  mix_early:          'Early deliveries — week to date',
  mix_late:           'Late deliveries — week to date',
  mix_otif_pass:    'OTIF passed — schedules delivered in-full within tolerance',
  mix_otif_pending: 'OTIF pending — still within late-tolerance window',
  mix_otif_fail:    'OTIF failed — past window without passing, or delivered short / out of window',
};

export type DrilldownEntity = 'schedule' | 'grn';

/** Which entity the modal queries for a given mode. ``day_late``,
 *  ``day_early``, ``day_on_time`` and the ``mix_*`` slices match the bar
 *  chart whose source is GoodsReceiptNote bucketed by ``receipt_date``
 *  + ``on_time_status``, so the drilldown queries GRNs directly. Pending
 *  and window/cost tiles stay schedule-keyed. */
export function getEntityForMode(mode: CurrentWeekDrilldownMode): DrilldownEntity {
  switch (mode) {
    case 'day_on_time':
    case 'day_early':
    case 'day_late':
    case 'mix_on_time':
    case 'mix_early':
    case 'mix_late':
      return 'grn';
    default:
      return 'schedule';
  }
}

export type DrilldownParamShape = Record<string, string | number | boolean>;

/** A single category tab inside a drilldown group. ``overlay`` adds extra
 *  query params on top of the mode's base params — used by KPI-tile groups
 *  (OTIF window) where every tab shares one mode but filters a different
 *  ``otif_status`` slice. */
export interface DrilldownTab {
  mode: CurrentWeekDrilldownMode;
  label: string;
  overlay?: Record<string, string>;
}

/** Category tab groups. Clicking any segment of a chart opens the modal with
 *  tabs for the WHOLE sibling group, so the user can step through every
 *  category (early / late / on-time / pending, or passed / pending / failed)
 *  without re-opening. Single-metric tiles (due / open / received / arrears /
 *  otif_this_week / otd_this_week) are not in any group → no tabs. */
const TAB_GROUPS: DrilldownTab[][] = [
  // "Deliveries by Day" stacked bar (carries dayIso through the tabs)
  [
    { mode: 'day_on_time', label: 'On time' },
    { mode: 'day_early',   label: 'Early' },
    { mode: 'day_late',    label: 'Late' },
    { mode: 'day_pending', label: 'Pending' },
  ],
  // OTD ring — week to date (GRN on_time_status)
  [
    { mode: 'mix_on_time', label: 'On time' },
    { mode: 'mix_early',   label: 'Early' },
    { mode: 'mix_late',    label: 'Late' },
  ],
  // OTIF ring — week to date (per-schedule otif_status)
  [
    { mode: 'mix_otif_pass',    label: 'Passed' },
    { mode: 'mix_otif_pending', label: 'Pending' },
    { mode: 'mix_otif_fail',    label: 'Failed' },
  ],
  // OTIF KPI tile — the full week, sliced by otif_status. Every tab keeps
  // the same window; the overlay replaces the base ``otif_status=due`` so
  // "All" is the ring's denominator (432/454 on DE = Passed + Failed) and
  // Passed / Failed are its parts. Pending sits alongside as the
  // not-yet-scored population the ring deliberately leaves out.
  [
    { mode: 'otif_this_week', label: 'All' },
    { mode: 'otif_this_week', label: 'Passed',  overlay: { otif_status: 'passed' } },
    { mode: 'otif_this_week', label: 'Pending', overlay: { otif_status: 'pending' } },
    { mode: 'otif_this_week', label: 'Failed',  overlay: { otif_status: 'failed' } },
  ],
];

/** Stable identity for a tab (mode + overlay) so groups whose tabs share a
 *  mode — the OTIF-tile group — can still mark the right tab active. */
export function tabKey(t: DrilldownTab): string {
  return `${t.mode}|${JSON.stringify(t.overlay ?? {})}`;
}

/** The sibling tab group a mode belongs to, or ``null`` for single-metric
 *  tiles that open without tabs. */
export function getTabGroup(mode: CurrentWeekDrilldownMode): DrilldownTab[] | null {
  return TAB_GROUPS.find((g) => g.some((t) => t.mode === mode)) ?? null;
}

export function buildCurrentWeekParams(args: BuildParamsArgs): DrilldownParamShape {
  const {
    mode, filters, weekStartIso, weekEndIso, otifStartIso, otifEndIso,
    todayIso, dayIso, arrearsBufferDays = 0,
  } = args;
  const entity = getEntityForMode(mode);

  // GRN endpoint uses different filter prefixes than the schedule endpoint.
  // For day_late / day_early / day_on_time / mix_*, query the GRN list
  // (``/purchasing/grns/``) so the drilldown matches the bar chart which
  // counts GRNs by ``receipt_date`` + ``on_time_status``.
  if (entity === 'grn') {
    const g: DrilldownParamShape = {
      po_line__po__erp_order_type: filters.erp_order_type,
    };
    if (filters.customer_id) g['po_line__po__customer'] = filters.customer_id;
    if (filters.supplier_id) g['po_line__po__supplier'] = filters.supplier_id;
    if (filters.part_number) g['po_line__part_number'] = filters.part_number;

    // Filter by on_time_status to match the clicked segment.
    // CR-6543 r2: the donut's "On time" count is PASSING_STATUS_SQL —
    // on_time + overridden_early + overridden_late (an accepted override
    // turns a negative verdict into a pass, CR-6518). Filtering the tab on
    // the exact 'on_time' value left overridden GRNs counted in the segment
    // but visible in no tab. Early/Late tabs stay exact-match: an
    // overridden row's status is REPLACED, so it no longer belongs there.
    if (mode === 'day_on_time' || mode === 'mix_on_time') {
      g['on_time_status__in'] = 'on_time,overridden_early,overridden_late';
    }
    else if (mode === 'day_early' || mode === 'mix_early')     g['on_time_status'] = 'early';
    else if (mode === 'day_late'  || mode === 'mix_late')      g['on_time_status'] = 'late';

    // Date axis = receipt_date (matches chart's x-axis).
    if (mode.startsWith('day_') && dayIso) {
      g['receipt_date__gte'] = dayIso;
      g['receipt_date__lte'] = dayIso;
    } else {
      // mix_* — week-to-date OTD window
      g['receipt_date__gte'] = otifStartIso;
      g['receipt_date__lte'] = otifEndIso;
    }
    return g;
  }

  const p: DrilldownParamShape = {
    exclude_line_zero: true,
    po_line__po__erp_order_type: filters.erp_order_type,
  };
  if (filters.customer_id) p['po_line__po__customer'] = filters.customer_id;
  if (filters.supplier_id) p['po_line__po__supplier'] = filters.supplier_id;
  if (filters.part_number) p['po_line__part_number'] = filters.part_number;

  switch (mode) {
    case 'due_this_week':
      p['schedule_date__gte'] = weekStartIso;
      p['schedule_date__lte'] = weekEndIso;
      // Every Cost tile counts from `scoped_schedules`, which scopes with
      // `s.status != 'cancelled'` — retired demand is not this week's
      // commitment. Without the same exclusion the list showed 3,281 rows
      // against a tile of 2,445 on DE (836 cancelled slots).
      p['exclude_status'] = 'cancelled';
      break;
    case 'open_this_week':
      p['schedule_date__gte'] = weekStartIso;
      p['schedule_date__lte'] = weekEndIso;
      // ``outstanding=true`` matches the tile's open_lines count
      // (schedule_qty − Σ allocated > 0) — what the SQL counts.
      p['outstanding'] = true;
      // Every Cost tile counts from `scoped_schedules`, which scopes with
      // `s.status != 'cancelled'` — retired demand is not this week's
      // commitment. Without the same exclusion the list showed 3,281 rows
      // against a tile of 2,445 on DE (836 cancelled slots).
      p['exclude_status'] = 'cancelled';
      break;
    case 'received_this_week':
      p['schedule_date__gte'] = weekStartIso;
      p['schedule_date__lte'] = weekEndIso;
      // Tile counts schedules with shipped_qty > 0 in window. Backend
      // filterset accepts shipped_qty__gt directly — count matches the
      // tile, not just a status proxy.
      p['shipped_qty__gt'] = 0;
      // Every Cost tile counts from `scoped_schedules`, which scopes with
      // `s.status != 'cancelled'` — retired demand is not this week's
      // commitment. Without the same exclusion the list showed 3,281 rows
      // against a tile of 2,445 on DE (836 cancelled slots).
      p['exclude_status'] = 'cancelled';
      break;
    case 'arrears':
      // Tile arrears: schedule_date < today AND balance_qty > 0 (the
      // dashboard's is_past_due predicate is strict `<` — today's
      // not-yet-shipped lines are "due today", not past-due). Use
      // balance_qty__gt=0 (matches the tile CTE column directly)
      // instead of outstanding=true (which recomputes from GRN
      // allocations and diverges when balance_qty is authoritative).
      //
      // Deliberately UNBOUNDED at the start: the arrears tile is the
      // cumulative portfolio figure (_arrears in
      // scheduled_dashboard_service.py — "ALL past-due open schedules
      // for the scope, not bounded to any week window"), so clamping
      // the drill-down to weekStartIso listed a fraction of what the
      // tile counted (586 past-due vs 234 listed on DE). Every other
      // mode here is a this-week metric and keeps its bounds.
      p['schedule_date__lt']  = isoMinusDays(todayIso, arrearsBufferDays);
      p['balance_qty__gt'] = 0;
      // The tile's `_arrears` SQL narrows further, and the drilldown has to
      // match or it lists rows its own tile deliberately dropped — 184 rows
      // against a tile reading 52 on DE.
      //
      // A cancelled slot is retired demand: its balance is a written-off
      // shortfall, not something anyone is still chasing. That distinction
      // matters most for the part-delivered lines the retire command closes —
      // they keep a real balance ON PURPOSE (so the shortfall stays visible),
      // which is exactly what made every one of them reappear here.
      p['exclude_status'] = 'cancelled';
      // A hold is a customer-side stop; the supplier is not in arrears for it.
      p['on_hold'] = false;
      // A staff OTIF override is an accepted exception — the tile drops these
      // (`AND NOT s.otif_overridden`) and so must the list.
      p['otif_overridden'] = false;
      break;
    case 'otif_this_week':
    case 'otd_this_week':
      // FULL week (Mon → Sun) — the same window the tile counts.
      //
      // The rings used to be measured over a separate "OTIF window"
      // (week-start → today, frozen at the fifth day). They are not any
      // more: ``_pack`` in scheduled_dashboard_service.py reads EVERY
      // schedule KPI — OTIF, OTD, Mix and Cost — from the full-week
      // bucket so one population feeds every tile and donut. The
      // drilldown was never moved with it, so it kept clamping to
      // ``otifStartIso → todayIso`` and listed a slice of what the ring
      // counted: on a Monday that window is a SINGLE day, and DE's ring
      // read 22 failed while its drilldown listed 1 (2026-09-07).
      // Bound to the tile's own window instead.
      p['schedule_date__gte'] = weekStartIso;
      p['schedule_date__lte'] = weekEndIso;
      // ``otif_status=due`` = the ring's denominator (passed + failed),
      // i.e. the tile's ``total_due`` / ``otd_due`` — both are the same
      // population in the SQL. Without it the untabbed "All" view listed
      // the whole week including Pending (2,342 rows against a ring
      // footer reading 432/454) and also kept OTIF-excluded POs that
      // every sibling tab drops, so All wasn't the sum of its parts
      // (1,912 vs 1,827 on DE). The Passed/Pending/Failed tab overlays
      // replace this value.
      p['otif_status'] = 'due';
      // On-hold slots are excluded from OTIF and OTD while held
      // (customer review 2026-07-08), so keep the drilldowns consistent
      // with the rings.
      p['on_hold'] = false;
      // Same reason, same rings: the OTIF/OTD SQL scopes with
      // `s.status != 'cancelled'`. Without this the drilldown listed every
      // cancelled slot in the window — 576 of 2,505 rows on one install —
      // and showed them as "Pending", because a cancelled slot has no
      // allocations for the status classifier to read.
      p['exclude_status'] = 'cancelled';
      break;
    case 'day_pending':
      // Schedules with outstanding balance for this day.
      //
      // The chart's "pending" series rolls all past-due (Sun→today)
      // schedules into today's bar, so clicking today opens the full
      // arrears+today bucket; clicking any future day shows only that
      // day. Mirrors the SQL in ``_deliveries_by_day``.
      if (dayIso && dayIso === todayIso) {
        p['schedule_date__gte'] = weekStartIso;
        p['schedule_date__lte'] = todayIso;
      } else if (dayIso) {
        p['schedule_date__gte'] = dayIso;
        p['schedule_date__lte'] = dayIso;
      } else {
        p['schedule_date__gte'] = weekStartIso;
        p['schedule_date__lte'] = weekEndIso;
      }
      p['outstanding'] = true;
      break;
    // day_on_time / day_early / day_late / mix_* are handled above by the
    // GRN entity branch — they never reach this switch.
    case 'day_on_time':
    case 'day_early':
    case 'day_late':
    case 'mix_on_time':
    case 'mix_early':
    case 'mix_late':
      // unreachable; fall-through guarded by getEntityForMode().
      break;
    case 'mix_otif_pass':
    case 'mix_otif_pending':
    case 'mix_otif_fail':
      // OTIF Mix — three-state schedule-keyed drilldown across the
      // full-week bucket. Filter by ``otif_status`` to match the slice.
      p['schedule_date__gte'] = weekStartIso;
      p['schedule_date__lte'] = weekEndIso;
      // On-hold slots are excluded from OTIF while held (2026-07-08).
      p['on_hold'] = false;
      if (mode === 'mix_otif_pass')         p['otif_status'] = 'passed';
      else if (mode === 'mix_otif_pending') p['otif_status'] = 'pending';
      else                                  p['otif_status'] = 'failed';
      break;
  }
  return p;
}
